import React, {Component} from 'react';
import autobind from 'autobind-decorator';
import {Col, Row} from 'react-bootstrap';

import {Glyphicon} from "../GlyphIcon";

import {service as SurveyQuestionDataTypeService} from "../../../Services/SurveyQuestionDataTypeService";
import {service as SurveyQuestionSecondaryFieldRequiredService} from "../../../Services/SurveyQuestionSecondaryFieldRequiredService";



import {
    ObjectUtils
} from "../../../Utils";

import {StreamBankScrollListenerEnhance} from "../Enhancements";
import {StreamBankAnchorLink} from "../StreamBankAnchorLink";


@StreamBankScrollListenerEnhance //This will rerender the element on scroll events... We want that since the pagescroll state is really part of the context of this component.
export class SurveyNavigation extends Component {
    constructor(props) {
        super(props);

        this.state = {
            surveyQuestionDataTypes: [],
            surveyQuestionSecondaryFieldRequireds: []
        };
    }

    componentDidMount() {
        Promise.all([
            SurveyQuestionDataTypeService.Get(),
            SurveyQuestionSecondaryFieldRequiredService.Get()
        ])
            .then(([types, reqs]) => {
                this.setState({
                    surveyQuestionDataTypes: types.result,
                    surveyQuestionSecondaryFieldRequireds: reqs.result
                });
            });
    }

    @autobind
    getSectionId(section) {
        return "sb-survey-section-" + section.surveySectionId;
    }

    @autobind
    isSectionAboveViewport(section) {
        const sectionId = this.getSectionId(section);
        const el = document.getElementById(sectionId);

        if(el) {
            const rect = el.getBoundingClientRect();
            const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
            const vertPassedView = (rect.bottom < windowHeight);

            return vertPassedView;
        } else {
            return true;
        }
    }

    @autobind
    isAnyPartOfSectionInViewport(section) {
        const sectionId = this.getSectionId(section);
        const el = document.getElementById(sectionId);

        if(el) {
            const rect = el.getBoundingClientRect();
            const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
            const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);

            return vertInView;
        } else {
            return false;
        }
    }

    @autobind
    isSectionLeavingViewport(section) {
        const sectionId = this.getSectionId(section);
        const el = document.getElementById(sectionId);

        if(el) {
            const rect = el.getBoundingClientRect();
            const vertPassedView = ((rect.top + 50) < 0);

            return vertPassedView;
        } else {
            return false;
        }
    }

    @autobind
    getSectionClassName(section) {
        return this.isSectionLeavingViewport(section) ? "sb-survey-form-navigation-passed": "sb-survey-form-navigation-future";
    }

    @autobind
    getQuestionDataType(question) {
        const { surveyQuestionDataTypes } = this.state || {};

        return surveyQuestionDataTypes.find(
            x => x.surveyQuestionDataTypeId === question.surveyQuestionDataTypeId
        );
    }

    @autobind
    getQuestionSecondaryRequiredStatus(question) {
        const { surveyQuestionSecondaryFieldRequireds } = this.state || {};

        return surveyQuestionSecondaryFieldRequireds.find(
            x => x.surveyQuestionSecondaryFieldRequiredId === question.surveyQuestionSecondaryFieldRequiredId
        );
    }

    @autobind
    isSecondaryQuestionAcceptable(answer, req, primaryLogic, secondaryLogic) {
        //if primary field isn't set we're in an imcompelte state.
        if(!primaryLogic(answer)) {
            return false;
        }

        //check the secondary field based on how the question is configured.
        switch(req.code) {
            case "yes":
                //since the primary field is good. We just need to make sure this is too.
                return secondaryLogic(answer);
            case "no":
                //we don't care about the second field. So we're good.
                return true;
            case "when_yes":
                if(answer.valueBool != null) {
                    if(answer.valueBool == true) {
                        return secondaryLogic(answer);
                    }

                    //if we're no then we can call this good.
                    return true;
                }

                //if bool isn't we should fail this. In theory we shouldn't hit this code, but I want to be safe.
                return false;
            case "when_no":
                if(answer.valueBool != null) {
                    if(answer.valueBool == false) {
                        return secondaryLogic(answer);
                    }

                    //if we're yes then we can call this good.
                    return true;
                }

                //if bool isn't we should fail this. In theory we shouldn't hit this code, but I want to be safe.
                return false;
            default:
                console.log("Unknown question requirement type to validate: " + req.code);
                break;
        }


        //if we've made it here something went wrong. Let's just fail things.
        return false;
    }

    @autobind
    isTextFileAcceptable(answer) {
        //if the file is set we're good.
        if(answer.fileUploadId != null) {
            return true;
        } else if (answer.valueText != null && answer.valueText != "") {
            //or if the text field is set.
            return true;
        }
        //the file and text field are also acceptable. so we don't need to check for that above.

        return false;
    }

    @autobind
    isSectionComplete(section) {
        const { Survey, CustomSections, CustomData, GetQuestionAnswer, ConservationAction } = this.props;
        const { surveyQuestionSecondaryFieldRequireds, surveyQuestionDataTypes } = this.state || {};

        //if we don't have the data on how to define completed questions we can only assume everything isn't finished.
        if(
            surveyQuestionSecondaryFieldRequireds?.length === 0 ||
            surveyQuestionDataTypes?.length === 0
        ) {
            return false;
        }

        let ret = true;

        if(CustomSections?.[section.code]?.isSectionComplete) {
            return CustomSections[section.code].isSectionComplete(Survey, section, CustomData);
        } else {
            section.SurveyQuestions.forEach((x) => {
                const req = this.getQuestionSecondaryRequiredStatus(x);
                const type = this.getQuestionDataType(x);
                const answer = this.props.GetQuestionAnswer(x);

                if(answer === null || type === null || req === null) {
                    ret = false;
                } else {
                    let localRet = true;

                    switch(type.code) {
                        case "text":
                            localRet = answer.valueText != null && answer.valueText != "";
                            break;
                        case "bool":
                            localRet = answer.valueBool != null;
                            break;
                        case "file":
                            localRet = answer.fileUploadId != null;
                            break;
                        case "number":
                            localRet = answer.valueNumeric != null && answer.valueNumeric !== "";
                            break;
                        case "webmap":
                            //todo: Ensure webmap id format
                            localRet = answer.valueText != null && answer.valueText != "";
                            break;
                        case "date":
                            localRet = answer.valueDate != null && answer.valueDate != "";
                            break;
                        case "date_bool":
                            localRet = this.isSecondaryQuestionAcceptable(
                                answer,
                                req,
                                (x) => x.valueBool != null,
                                (x) => x.valueDate != null && x.valueDate != ""
                            );
                            break;
                        case "text_bool":
                            localRet = this.isSecondaryQuestionAcceptable(
                                answer,
                                req,
                                (x) => x.valueBool != null,
                                (x) => x.valueText != null && x.valueText !== ""
                            );
                            break;
                        case "file_bool":
                            localRet = this.isSecondaryQuestionAcceptable(
                                answer,
                                req,
                                (x) => x.valueBool != null,
                                (x) => x.fileUploadId != null
                            );
                            break;
                        case "text_file":
                            localRet = this.isTextFileAcceptable(answer);
                        case "permit_bool":
                            localRet = false;

                            if(answer.valueBool === 0 && answer.valueText) { // if no is selected
                                localRet = true;
                            } else if (answer.valueBool === 1) {
                                // or yes and the permit is filled out.
                                const hasPermit = ConservationAction?.ConservationActionPermits.find(caPermit => caPermit.Permit?.permitTypeId === x.permitTypeId);

                                if (hasPermit) {
                                    localRet = true;
                                }
                            }
                            break;
                        case "label":
                            break;
                        default:
                            console.log("Unknown data type to validate: " + type.code);
                            localRet = false;
                            break;
                    }

                    ret = ret && localRet;
                }
            });
        }

        return ret;
    }

    @autobind
    getSections() {
        const sections = ObjectUtils.Clone(this.props.Sections);
        let ret = [];

        sections.forEach((x) => {
            let existing = ret.find((y) => x.name === y.name);

            if(existing) {
                existing.SurveyQuestions = existing.SurveyQuestions.concat(x.SurveyQuestions);
            } else {
                ret.push(x);
            }
        });

        return ret;
    }

    render() {
        return <Row className="sb-survey-form-navigation-control">
            {
                this.getSections().map((x) => {
                    if(!x.name) {
                        return null;
                    }

                    const anchor = this.getSectionId(x);
                    const activeClass = this.getSectionClassName(x);
                    const isSectionComplete = this.isSectionComplete(x);

                    let classes = "sb-survey-form-navigation-control-record " + activeClass;

                    if(isSectionComplete) {
                        classes += " sb-survey-form-navigation-control-record-completed";
                    }

                    return <Col key={x.surveySectionId} className={classes} xs={12}>
                        <StreamBankAnchorLink Anchor={anchor}>
                            <span>{x.name}</span>
                            {
                                isSectionComplete ?
                                    <Glyphicon glyph="check-circle" className="ml-2" />:
                                    null
                            }
                        </StreamBankAnchorLink>
                    </Col>;
                })
            }
        </Row>;
    }
}


